import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Modal from "@material-ui/core/Modal";
import { Document, Page, pdf, PDFViewer } from "@react-pdf/renderer";
import { useState } from "react";
import { GrDocumentPdf } from "react-icons/gr";
import { LuX } from "react-icons/lu";
import { toastError } from "../../../utils";
import PdfFooter from "../PortInformation/pdfFooter";
import PdfHeader from "../PortInformation/pdfHeader";
import InvoicePdf from "./InvoicePdf";
import LaytimeStatementPDF from "./LaytimeStatementPdf";
import StatementOfFactsPdf from "./StatementOfFactsPdf";
import { createInvoice } from "./action";
import SustainabilityPdf from "./sustainabilityPdf";
import CommonPdfPreviewModal from "../../../components/Common/CommonPdfPreviewModal";

export const DownloadPdf = (props) => {
  const { pdfData, isLaytimeStatement, onDownloadPdf } = props;
  const [open, setOpen] = useState(false);
  const [pdfName, setPdfName] = useState(
    `${new Date().getTime().toString(16)}${Math.floor(
      Math.random() * 16777215
    ).toString(16)}.pdf`
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const downloadFile = async () => {
    const { _id } = await onDownloadPdf()
    const doc = (
      <Document>
        <Page
          size="A4"
          orientation={isLaytimeStatement ? "landscape" : "portrait"}
        >
          <PdfHeader />
          {isLaytimeStatement ? (
            <LaytimeStatementPDF laytimeStatementDetails={pdfData} pdfSaveId={_id} />
          ) : (
            <StatementOfFactsPdf state={pdfData} pdfSaveId={_id} />
          )}
          <PdfFooter />
        </Page>
      </Document>
    );

    const pdfBlob = await pdf(doc).toBlob();
    const url = window.URL.createObjectURL(pdfBlob);
    const element = document.createElement("a");
    element.href = url;
    element.setAttribute("download", pdfName);
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const generatePdf = async () => {
    try {
      handleOpen();
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return (
    <div className="inline-flex">
      <button
        onClick={generatePdf}
        type="button"
        className="flex items-center gap-1.5 px-2.5 py-0.5 h-[22px] text-[11px] font-medium text-white bg-[#313C4C] border border-[#313C4C] rounded-none cursor-pointer hover:bg-[#0c2f50] transition-colors m-0"
      >
        <GrDocumentPdf size={12} />
        <span>Download PDF</span>
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pdf-guide-title"
      >
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "calc(100% - 32px)",
            maxWidth: "620px",
            maxHeight: "calc(100vh - 32px)",
            backgroundColor: "#ffffff",
            boxShadow: "0px 10px 30px rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-[#f8fafc]">
            <h2 id="pdf-guide-title" className="text-base font-semibold text-[#313C4C]">
              Download {isLaytimeStatement ? "Laytime Statement" : "Statement of Facts"} PDF
            </h2>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="p-2 rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition-colors"
            >
              <LuX className="h-5 w-5" />
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto px-4 py-4 text-[12px] text-slate-700">
            <div className="border border-slate-200 bg-slate-50 px-3 py-3">
              <div className="font-semibold text-slate-900 mb-1">What is this PDF?</div>
              <p>A printable copy of the {isLaytimeStatement ? "Laytime Statement" : "Statement of Facts"} exactly as it is filled in right now.</p>
            </div>
            <div className="font-semibold text-slate-900 mt-4 mb-2">How to use it</div>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Check that all details in the form are complete and correct.</li>
              <li>Click <strong>Download PDF</strong> below to save the file.</li>
              <li>Share or print the PDF as needed.</li>
            </ol>
            <div className="font-semibold text-slate-900 mt-4 mb-2">Where can I use this PDF?</div>
            <p className="mb-2">You can load the downloaded PDF into an operation so its {isLaytimeStatement ? "Laytime Statement" : "Statement of Facts"} is filled in from the file:</p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>Go to the <strong>Operations</strong> page and open the operation this {isLaytimeStatement ? "Laytime Statement" : "Statement of Facts"} belongs to.</li>
              <li>
                In the operation's cargo operation table, find the row you want and click <strong>View</strong> in the <strong>{isLaytimeStatement ? "Laytimes" : "Statement of Facts"}</strong> column
                {isLaytimeStatement ? " (View is shown on Loading rows only)." : "."}
              </li>
              <li>A <strong>{isLaytimeStatement ? "Laytime Statement" : "Statement of Facts"}</strong> window opens. Click <strong>Upload PDF</strong> at the top of that window and choose the PDF you downloaded.</li>
            </ol>
          </div>

          <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-white">
            <Button onClick={handleClose} variant="outlined">
              Close
            </Button>
            <Button onClick={downloadFile} variant="contained" color="primary">
              Download PDF
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export const DownloadInvoice = ({
  setIsLoading,
  invoiceData,
  bankDetails,
  invoiceDetail,
  refetch,
  opsId,
  cargoType,
  contractType,
  handleModalClose,
}) => {
  const [open, setOpen] = useState(false);
  const [pdfName] = useState(
    `invoice_${new Date().getTime().toString(16)}.pdf`
  );
  const [finalPdfData, setFinalPdfData] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const downloadFile = async () => {
    try {
      setIsLoading?.(true);
      const items = invoiceDetail || [];
      const separated = {
        cargo: items
          .filter((item) => item?.invoiceDetail?.isCargo)
          .map((item) => ({
            ...item,
            _id: item?.invoiceDetail?._id,
            cargoContractId: item?.invoiceDetail?.cargoContractId,
            invoiceDetail: { ...item?.invoiceDetail, vesselType: item?.vesselType },
          })),
        vessel: items
          .filter((item) => !item?.invoiceDetail?.isCargo)
          .map((item) => ({
            ...item,
            vesselContractId: item?.invoiceDetail?.vesselContractId,
            _id: item?.invoiceDetail?._id,
            invoiceDetail: { ...item?.invoiceDetail, vesselType: item?.vesselType },
          })),
      };
      await createInvoice({ finalPdfData, separated, cargoType, contractType });
      refetch?.();
      handleClose();
      handleModalClose?.();
    } catch (error) {
      toastError("Error downloading PDF");
      console.error("Error downloading PDF:", error);
    } finally {
      setIsLoading?.(false);
    }
  };

  const generatePdf = () => {
    try {
      const finalData = {
        ...invoiceData,
        opsId,
        bankDetails,
        invoiceDetail: (invoiceData?.invoiceDetail || []).map((item) => ({
          ...item,
          isInvoiceGenerated: true,
        })),
      };
      setFinalPdfData(finalData);
      handleOpen();
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const documentToPreview = (
    <Document>
      <Page size="A4" orientation="portrait" style={{ paddingHorizontal: 16, paddingTop: 10, paddingBottom: 16, backgroundColor: "#ffffff" }}>
        <PdfHeader />
        <InvoicePdf invoiceDetails={finalPdfData || invoiceData} />
      </Page>
    </Document>
  );

  return (
    <>
      <button
        onClick={generatePdf}
        type="button"
        className="bg-[#313C4C] hover:bg-[#0d508f] text-white px-3 py-1 text-[11px] font-medium transition shadow-xs cursor-pointer flex items-center gap-1.5 rounded-none"
      >
        <GrDocumentPdf size={12} />
        <span>Generate Invoice</span>
      </button>

      <CommonPdfPreviewModal
        open={open}
        onClose={handleClose}
        title="Invoice Preview"
        subtitle="A4 Portrait Invoice • Smart Sea Freight"
        document={documentToPreview}
        onDownload={downloadFile}
        downloadFileName={pdfName}
        downloadButtonText="Download Invoice"
      />
    </>
  );
};

export const DownloadSustainabilityPdf = ({ formData, processedCargoDetails, cargoPartyName }) => {
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfName] = useState(
    `Sustainability-${formData?.voyageNumber || 'report'}-${new Date().getTime().toString(16)}.pdf`
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const downloadFile = async () => {
    try {
      if (!pdfUrl) return;

      const element = document.createElement("a");
      element.href = pdfUrl;
      element.setAttribute("download", pdfName);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      // Close modal after download
      handleClose();
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Error downloading PDF. Please try again.");
    }
  };

  const generatePdf = async () => {
    try {
      setIsGenerating(true);

      const doc = (
        <Document>
          <Page
            size="A4"
            orientation="portrait"
          >
            <PdfHeader />
            <SustainabilityPdf
              formData={formData}
              processedCargoDetails={processedCargoDetails}
              cargoPartyName={cargoPartyName}
            />
            <PdfFooter />
          </Page>
        </Document>

      );

      const pdfBlob = await pdf(doc).toBlob();
      const url = window.URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      handleOpen();
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex items-center justify-end mb-1">
      {/* Generate PDF Button */}
      <button
        onClick={generatePdf}
        type="button"
        disabled={isGenerating}
        className="flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-medium text-slate-700 bg-white border border-slate-300 rounded-none cursor-pointer hover:bg-slate-50 transition-colors shadow-none"
      >
        <GrDocumentPdf className="w-3.5 h-3.5 text-slate-600" />
        <span>Export PDF</span>
      </button>

      {/* Modal for PDF Preview */}
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Sustainability Preview
              </h2>
              <button
                onClick={handleClose}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <LuX className="h-5 w-5" />
              </button>
            </div>

            {/* PDF Preview */}
            <div className="flex-1 overflow-hidden">
              {pdfUrl ? (
                <PDFViewer width="100%" height="100%" className="border-0">
                  <Document>
                    <Page
                      size="A4"
                      orientation="portrait"
                    >
                      <PdfHeader />
                      <SustainabilityPdf
                        formData={formData}
                        processedCargoDetails={processedCargoDetails}
                        cargoPartyName={cargoPartyName}
                      />
                      <PdfFooter />
                    </Page>
                  </Document>
                </PDFViewer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg className="animate-spin h-10 w-10 text-sky-600 mx-auto mb-4" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <p className="text-slate-600">Generating preview...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <Button
                onClick={handleClose}
                variant="outlined"
                style={{ marginRight: "8px" }}
              >
                Close
              </Button>
              <Button
                onClick={downloadFile}
                variant="contained"
                color="primary"
              >
                Download PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
